import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import StoryCard from './StoryCard'
import SkeletonLoader from './SkeletonLoader'
import axios from 'axios'
import { useBlocks } from '../hooks/useBlocks'
import { useAuth } from '../context/AuthContext'

const BADGES = [
  { label: '🔥 Hot',     color: '#EC4899' },
  { label: '⭐ Rising',  color: '#F59E0B' },
  { label: '📚 Long',    color: '#7C3AED' },
  { label: '✨ New',     color: '#0D9488' },
  { label: '🚀 Trending', color: '#1D4ED8' },
]

const GENRES = ['All', 'Fantasy', 'Romance', 'Sci-Fi', 'Anime', 'Mystery', 'Horror', 'Other']
const RANK_CLASS = ['', 'trending-item__rank--1', 'trending-item__rank--2', 'trending-item__rank--3']

export default function StoryFeed() {
  const [stories, setStories]   = useState([])
  const [trending, setTrending] = useState([])
  const [activeGenre, setActiveGenre] = useState('All')
  const [loading, setLoading]   = useState(true)
  const blockedIds = useBlocks()
  const { getToken } = useAuth()

  useEffect(() => {
    async function fetchStories() {
      try {
        setLoading(true)
        const token = await getToken().catch(() => null)
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const [storiesRes, trendingRes] = await Promise.allSettled([
          axios.get('/api/stories?sort=hot&limit=100', { headers }),
          axios.get('/api/stories/trending', { headers }),
        ])
        if (storiesRes.status === 'fulfilled') {
          setStories(storiesRes.value.data ?? [])
        }
        if (trendingRes.status === 'fulfilled') {
          setTrending((trendingRes.value.data ?? []).slice(0, 5))
        }
      } finally {
        setLoading(false)
      }
    }
    fetchStories()
  }, [])

  const filtered = stories
    .filter(s => !blockedIds.has(s.userId))
    .filter(s => activeGenre === 'All' || s.genre?.includes(activeGenre))

  return (
    <>
      {/* ── Recent stories ── */}
      <div className="section">
        <div className="section__header">
          <div>
            <h2 className="section__title">Recent Stories</h2>
            <p className="section__subtitle">
              Fresh chapters, new beginnings, and hidden gems
            </p>
          </div>
          <Link to="/browse" className="section__link">Browse all →</Link>
        </div>

        <div className="genre-chips">
          {GENRES.map((g) => (
            <button
              key={g}
              className={`genre-chip${activeGenre === g ? ' genre-chip--active' : ''}`}
              onClick={() => setActiveGenre(g)}
            >
              {g}
            </button>
          ))}
        </div>

        {loading ? (
          <SkeletonLoader type="story-grid" />
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-faint)' }}>
            No stories in this genre yet. Be the first to write one!
          </div>
        ) : (
          <div className="story-grid story-grid--dense">
            {filtered.map((story, i) => (
              <StoryCard key={story.id} story={story} index={i} />
            ))}
          </div>
        )}
      </div>

      {/* ── Trending ── */}
      <div className="trending-strip">
        <div className="section">
          <div className="section__header">
            <div>
              <h2 className="section__title">Trending This Week</h2>
              <p className="section__subtitle">The stories everyone is reading</p>
            </div>
            <Link to="/browse?sort=liked" className="section__link">See all →</Link>
          </div>

          {trending.length > 0 ? (
            <div className="trending-list">
              {trending.map((item, i) => {
                const badge = BADGES[i] ?? BADGES[BADGES.length - 1]
                return (
                  <Link key={item.id} to={`/story/${item.id}`} style={{ textDecoration: 'none' }}>
                    <div className="trending-item">
                      <span className={`trending-item__rank ${RANK_CLASS[i + 1] ?? 'trending-item__rank--other'}`}>
                        {i + 1}
                      </span>
                      <div className="trending-item__cover">
                        {item.coverUrl
                          ? <img src={item.coverUrl} alt={item.title} loading="lazy" />
                          : null}
                      </div>
                      <div className="trending-item__info">
                        <div className="trending-item__title">{item.title}{item.sexuallyExplicit && <span className="explicit-inline-badge">18+</span>}</div>
                        <div className="trending-item__meta">
                          by {item.author} · {item.fandom}
                        </div>
                      </div>
                      <span
                        className="trending-item__badge"
                        style={{
                          background: badge.color + '22',
                          color: badge.color,
                          border: `1px solid ${badge.color}44`,
                        }}
                      >
                        {badge.label}
                      </span>
                    </div>
                  </Link>
                )
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-faint)', fontSize: '0.88rem' }}>
              No trending stories yet — be the first to write one!
            </div>
          )}
        </div>
      </div>
    </>
  )
}
